<script setup>
// NOTICE: KAPTION-INTEGRATION
// AI Features Pill component with two-gate system (feature flags + conversation toggles)

import { ref, computed, toRef } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useKaptionAIFeaturesPreferences } from 'dashboard/composables/useKaptionAIFeaturesPreferences';
import { useKaptionScheduledMessages } from 'dashboard/composables/useKaptionScheduledMessages';
import { useKaptionMessagePrivacyMode } from 'dashboard/composables/useKaptionPrivacyMode';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { onClickOutside } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import KaptionToggleSwitch from './KaptionToggleSwitch.vue';
import KaptionCircleFlag from 'dashboard/components-next/flag/KaptionCircleFlag.vue';
import KaptionLanguageSelectionModal from './KaptionLanguageSelectionModal.vue';
import KaptionScheduledMessagesModal from './KaptionScheduledMessagesModal.vue';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
});

const { t } = useI18n();

const store = useStore();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const { canUseFeature } = useFeaturePermissions();
const { uiSettings } = useUISettings();
// NOTICE: KAPTION-INTEGRATION - Use toRef to create reactive reference from prop
// This ensures preferences reload when switching conversations
const conversationIdRef = toRef(props, 'conversationId');
const {
  translationEnabled,
  suggestionsEnabled,
  setTranslation,
  setSuggestions,
} = useKaptionAIFeaturesPreferences(conversationIdRef);

const {
  scheduledMessages,
  scheduledMessagesCount,
  isFetching: isFetchingScheduled,
  isCancelling: isCancellingScheduled,
  cancelScheduledMessage,
} = useKaptionScheduledMessages(conversationIdRef);

const {
  privacyEnabled: conversationPrivacyEnabled,
  togglePrivacy: toggleConversationPrivacy,
} = useKaptionMessagePrivacyMode();

const isExpanded = ref(false);
const pillRef = ref(null);
const showLanguageModal = ref(false);
const showScheduledModal = ref(false);

// Get current chat from store
const currentChat = computed(() => store.getters.getSelectedChat);

// Get conversation language from additional_attributes
// NOTICE: KAPTION-INTEGRATION - Always read from server state, never assume default
// This prevents sync issues where frontend defaults differ from backend detection
const conversationLanguage = computed(() => {
  return (
    currentChat.value?.additional_attributes?.conversation_language ||
    currentChat.value?.additionalAttributes?.conversationLanguage
  );
});

// NOTICE: KAPTION-INTEGRATION - languageFlag computed removed, using KaptionCircleFlag component instead

// Gate 1: Check global feature flags
// Gate 2: Check user preferences from ui_settings (master switch in topbar settings)
// feature-permissions.AI.2 - gate translation control on client-managed permission
const showTranslation = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_LIVE_TRANSLATION') &&
    canUseFeature('live_translation') &&
    (uiSettings.value?.kaption_live_translation_enabled ?? true)
);

const showSuggestions = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_AI_SUGGESTIONS') &&
    // NOTICE: KAPTION-INTEGRATION - feature-permissions.AI.1: align the topbar toggle
    // with the panel + backend gate (was flag-only, so it could offer a toggle for a
    // feature the account isn't permitted to use).
    canUseFeature('ai_reply_suggestions') &&
    (uiSettings.value?.kaption_ai_suggestions_enabled ?? true)
);

const showScheduledMessages = computed(() =>
  isKaptionFlagEnabled('KAPTION_SCHEDULED_MESSAGES')
);

// feature-permissions.AI.7 - gate privacy toggle on client-managed permission
const showPrivacy = computed(
  () =>
    isKaptionFlagEnabled('KAPTION_PRIVACY_MODE') &&
    canUseFeature('privacy_mode')
);

// Show pill if at least one feature flag is enabled
const showPill = computed(
  () =>
    showTranslation.value ||
    showSuggestions.value ||
    showScheduledMessages.value ||
    showPrivacy.value
);

// Toggle handlers with event emission
const toggleTranslation = () => {
  const newValue = !translationEnabled.value;
  setTranslation(newValue);

  // Emit event to notify other components (like LiveReplyTranslation)
  emitter.emit('kaption-translation-toggled', {
    conversationId: props.conversationId,
    enabled: newValue,
  });

  // Close language modal when disabling translation
  if (!newValue) {
    showLanguageModal.value = false;
  }
};

const toggleSuggestions = () => {
  const newValue = !suggestionsEnabled.value;
  setSuggestions(newValue);

  // NOTICE: KAPTION-INTEGRATION - Emit event to notify other components (like KaptionChatSuggestions)
  emitter.emit('kaption-suggestions-toggled', {
    conversationId: props.conversationId,
    enabled: newValue,
  });
};

const handlePrivacyClick = e => {
  e?.stopPropagation();
  toggleConversationPrivacy();
};

// Open language modal
const openLanguageModal = e => {
  if (!canUseFeature('conversation_language_selection')) return;
  e?.stopPropagation(); // Prevent pill collapse
  isExpanded.value = false; // Close the pill when opening modal
  showLanguageModal.value = true;
};

// Close language modal
const closeLanguageModal = () => {
  showLanguageModal.value = false;
};

// Open scheduled messages modal
const openScheduledModal = e => {
  e?.stopPropagation();
  isExpanded.value = false;
  showScheduledModal.value = true;
};

// Close scheduled messages modal
const closeScheduledModal = () => {
  showScheduledModal.value = false;
};

// Close pill when clicking outside
onClickOutside(pillRef, () => {
  isExpanded.value = false;
});

// Expand/collapse handlers
const expandPill = () => {
  isExpanded.value = true;
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div v-if="showPill" ref="pillRef" class="pill-container">
    <!-- Collapsed State -->
    <div
      v-if="!isExpanded"
      class="pill-collapsed bg-[#fafafa] dark:bg-[#000000]"
      @click.stop="expandPill"
    >
      <span class="pill-icon">✨</span>
      <span
        v-if="showTranslation"
        class="pill-feature-icon"
        :class="{ 'pill-feature-icon-disabled': !translationEnabled }"
      >
        <KaptionCircleFlag
          v-if="conversationLanguage"
          :language-code="conversationLanguage"
          size="size-4"
        />
        <span v-else>🌐</span>
      </span>
      <span
        v-if="showSuggestions"
        class="pill-feature-icon"
        :class="{ 'pill-feature-icon-disabled': !suggestionsEnabled }"
      >
        💡
      </span>
      <span
        v-if="showPrivacy"
        class="pill-feature-icon cursor-pointer"
        :class="{ 'pill-feature-icon-disabled': !conversationPrivacyEnabled }"
        @click="handlePrivacyClick"
      >
        <i
          class="size-4"
          :class="
            conversationPrivacyEnabled ? 'i-lucide-eye-off' : 'i-lucide-eye'
          "
        />
      </span>
      <span v-if="showScheduledMessages" class="pill-feature-icon relative">
        <i class="i-lucide-calendar-clock size-4" />
        <span
          v-if="scheduledMessagesCount > 0"
          class="absolute -top-1.5 -right-1.5 h-3.5 min-w-[14px] px-1 flex items-center justify-center text-[9px] font-medium rounded-full bg-n-green-9 text-white"
        >
          {{ scheduledMessagesCount > 9 ? '9+' : scheduledMessagesCount }}
        </span>
      </span>
    </div>

    <!-- Expanded State -->
    <div v-else class="pill-expanded bg-[#fafafa] dark:bg-[#000000]">
      <div class="pill-content">
        <!-- Translation Toggle -->
        <div v-if="showTranslation" class="feature-row">
          <div class="feature-info">
            <span
              class="feature-icon"
              :class="{ 'feature-icon-disabled': !translationEnabled }"
            >
              <KaptionCircleFlag
                v-if="conversationLanguage"
                :language-code="conversationLanguage"
                size="size-5"
              />
              <span v-else>🌐</span>
            </span>
            <span class="feature-label text-[#374151] dark:text-[#e5e7eb]">
              {{ t('CONVERSATION.LIVE_TRANSLATION.LABEL') }}
            </span>
          </div>
          <div class="feature-controls">
            <!-- feature-permissions.AI.5 - gate language-selection trigger on permission -->
            <button
              v-if="
                translationEnabled &&
                canUseFeature('conversation_language_selection')
              "
              v-tooltip="t('CONVERSATION.LIVE_TRANSLATION.CHANGE_LANGUAGE')"
              class="feature-icon-button"
              @click="openLanguageModal"
            >
              <i class="i-lucide-settings feature-icon-settings" />
            </button>
            <KaptionToggleSwitch
              :model-value="translationEnabled"
              @update:model-value="toggleTranslation"
            />
          </div>
        </div>

        <!-- Suggestions Toggle -->
        <div v-if="showSuggestions" class="feature-row">
          <div class="feature-info">
            <span
              class="feature-icon"
              :class="{ 'feature-icon-disabled': !suggestionsEnabled }"
            >
              💡
            </span>
            <span class="feature-label text-[#374151] dark:text-[#e5e7eb]">
              {{ t('CONVERSATION.AI_SUGGESTIONS.LABEL') }}
            </span>
          </div>
          <KaptionToggleSwitch
            :model-value="suggestionsEnabled"
            @update:model-value="toggleSuggestions"
          />
        </div>

        <!-- Privacy Mode Toggle -->
        <div v-if="showPrivacy" class="feature-row">
          <div class="feature-info">
            <span
              class="feature-icon"
              :class="{ 'feature-icon-disabled': !conversationPrivacyEnabled }"
            >
              <i
                class="size-5"
                :class="
                  conversationPrivacyEnabled
                    ? 'i-lucide-eye-off'
                    : 'i-lucide-eye'
                "
              />
            </span>
            <span class="feature-label text-[#374151] dark:text-[#e5e7eb]">
              {{ t('CONVERSATION.KAPTION_PRIVACY_MODE.LABEL') }}
            </span>
          </div>
          <KaptionToggleSwitch
            :model-value="conversationPrivacyEnabled"
            @update:model-value="toggleConversationPrivacy"
          />
        </div>

        <!-- Scheduled Messages Row -->
        <div
          v-if="showScheduledMessages"
          class="feature-row cursor-pointer group"
          @click="openScheduledModal"
        >
          <div class="feature-info">
            <span class="feature-icon">
              <i class="i-lucide-calendar-clock size-5 text-n-green-10" />
            </span>
            <span class="feature-label text-[#374151] dark:text-[#e5e7eb]">
              {{ t('CONVERSATION.KAPTION_SCHEDULED_MESSAGES.MODAL_TITLE') }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <span
              v-if="scheduledMessagesCount > 0"
              class="h-5 min-w-[20px] px-1.5 flex items-center justify-center text-[11px] font-bold rounded-full bg-n-green-9 text-white shadow-sm"
            >
              {{ scheduledMessagesCount }}
            </span>
            <i
              class="i-lucide-chevron-right size-4 text-n-slate-10 group-hover:text-n-slate-12 transition-colors"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Language Selection Modal - Outside pill container to avoid pointer-events issues -->
  <KaptionLanguageSelectionModal
    v-model:show="showLanguageModal"
    :conversation-id="conversationId"
    @close="closeLanguageModal"
  />

  <!--
    NOTICE: KAPTION-INTEGRATION
    Keep the viewport-level modal outside Warm Paper's clipped conversation card.
  -->
  <Teleport to="body">
    <KaptionScheduledMessagesModal
      v-model:show="showScheduledModal"
      :scheduled-messages="scheduledMessages"
      :is-fetching="isFetchingScheduled"
      :is-cancelling="isCancellingScheduled"
      @close="closeScheduledModal"
      @cancel="cancelScheduledMessage"
    />
  </Teleport>
</template>

<style scoped lang="scss">
.pill-container {
  position: sticky;
  top: 1rem;
  z-index: 40; /* NOTICE: KAPTION-INTEGRATION - Above campaign pill (39) but below modals */
  display: flex;
  justify-content: center;
  align-items: flex-start;
  /* No background, no padding, no margin, no height - container is just for positioning */
  background: none;
  height: 0;
  padding: 0;
  margin: 0;
  pointer-events: none;
  width: 100%;
}

/* Re-enable pointer events on the pill itself */
.pill-collapsed,
.pill-expanded {
  pointer-events: auto;
}

.pill-collapsed {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s ease;
  @apply shadow-md;
  /* Position the pill with margin-top */
  margin-top: 16px;
  /* Solid background */
  position: relative;
  z-index: 1;
}

.pill-collapsed:hover {
  @apply shadow-lg;
  transform: translateY(-1px);
}

.pill-icon {
  font-size: 1rem;
}

.pill-feature-icon {
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    opacity 0.2s ease,
    filter 0.2s ease;
}

.pill-feature-icon-disabled {
  opacity: 0.4;
  filter: grayscale(100%);
}

.pill-expanded {
  padding: 1rem;
  border-radius: 1rem;
  min-width: 280px;
  max-width: 320px;
  @apply shadow-md;
  /* Position the expanded pill with margin-top */
  margin-top: 16px;
  /* Solid background */
  position: relative;
  z-index: 1;
}

.pill-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.feature-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.feature-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.feature-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.feature-icon {
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    opacity 0.2s ease,
    filter 0.2s ease;
}

.feature-icon-disabled {
  opacity: 0.4;
  filter: grayscale(100%);
}

.feature-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.feature-icon-button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
}

.feature-icon-button:hover {
  transform: scale(1.1);
}

.feature-icon-settings {
  font-size: 0.875rem;
  color: #6b7280;
  @apply dark:text-n-slate-11;
  transition: color 0.15s ease;
}

.feature-icon-button:hover .feature-icon-settings {
  color: #374151;
  @apply dark:text-n-slate-12;
}
</style>
